import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { reduxForm, FormSection, Field } from 'redux-form';
import PropTypes from 'prop-types';
import { keys, values, snakeCase } from 'lodash';
import { round6AndFormatValue, round6 } from 'utilities/math';

import Button from 'components/Button';
import FieldsOneSampleT from './FormOneSampleT';
import FieldsTwoSampleT from './FormTwoSampleT';
import FieldsOneSampleProportion from './FormOneSampleProportion';
import FieldsTwoSampleProportion from './FormTwoSampleProportion';
import FieldsPairedT from './FormPairedT';
import FieldsChiSquare from './FormChiSquare';
import FieldsAnova from './FormAnova';
import Dropdown from './Dropdown';
import { CheckBox } from 'components/CheckBox';

import { setHasNumericalVariablesEmptyCell, setHasCategoricalVariablesEmptyCell } from 'store/scenes/inferentialStatistics/actions';
import {
  isInferentialStatisticsCalculated,
  generateSamples,
  handleResetClick,
  getActiveIndexTabAnova
} from 'store/scenes/inferentialStatistics/selectors';
import {
  getSummary,
  getFrequencyDataSet,
  getFrequencyDataSetLength,
  getVisibleNumericalSelectedParametersNames,
  getVisibleCategoricalSelectedParametersNames,
  getFirstAndSecondNumericalFilteredParameterValuesNotEmpty
} from 'store/dataset';

import {
  isPairedTInvalidForTwoGroups
} from 'store/form/inferentialStatistics/pairedT/selectors';

import {
  INFERENTIAL_STATISTICS,
  ONE_SAMPLE_T,
  ONE_SAMPLE_PROPORTION,
  TWO_SAMPLE_T,
  TWO_SAMPLE_PROPORTION,
  PAIRED_T,
  CHI_SQUARE_GOODNESS_OF_FIT,
  CHI_SQUARE_TEST_FOR_INDEPENDENCE,
  CHI_SQUARE_TEST_FOR_HOMOGENEITY,
  EQUAL_PROPORTION,
  ANOVA,
  optionsInferentialProcedure
} from '../Constants';
import {
  valueAnova,
  valuePairedT,
  valueOneSampleT,
  valueTwoSampleT,
  valueOneSampleProportion,
  valueTwoSampleProportion,
  valueInferentialStatisticsForm
} from './defaultValueForProcedures';
import ProcedureTypeRadioButtons from './ProcedureTypeRadioButtons';

import {
  resetParametersInferentialStatistics
} from 'store/scenes/inferentialStatistics';
import {
  getInferentialStatistics
} from 'store/form/inferentialStatistics';

import { calculateDefaultValueForPairedT } from 'calculators/inferentialStatistics/pairedT';


const SettingsFormComponent = ({ isFrequencyOrContingency, ...props }) => {
  const dispatch = useDispatch();

  const summary = useSelector(state => getSummary(state));
  const numericalSummary = summary ? summary.numericalSummary : null;
  const categoricalSummary = summary ? summary.categoricalSummary : null;

  const isCalculated = useSelector(state => isInferentialStatisticsCalculated(state));
  const frequencyDataset = useSelector(state => getFrequencyDataSet(state));
  const frequencyDataSetLength = useSelector(state => getFrequencyDataSetLength(state));
  const categoricalSelectedParametersNames = useSelector(state => getVisibleCategoricalSelectedParametersNames(state));
  const numericalSelectedParametersNames = useSelector(state => getVisibleNumericalSelectedParametersNames(state));
  const firstAndSecondNumericalVariable = useSelector(state => getFirstAndSecondNumericalFilteredParameterValuesNotEmpty(state));
  const inferentialStatistics = useSelector(state => getInferentialStatistics(state));
  const activeIndexTabAnova = useSelector(state => getActiveIndexTabAnova(state));
  const isPairedTInvalid = useSelector(state => isPairedTInvalidForTwoGroups(state));

  const [isValidationChiSquareError, setIsValidationChiSquareError] = useState(false);
  useEffect(() => {
    const {
      dirty,
      initialize
    } = props;

    if (!dirty) {
      let defaultInferentialStatistics = ONE_SAMPLE_T;
      let observedCounts = '';
      let defaultValuePairedT = {};
      let defaultValueOneSampleT = valueOneSampleT;
      let defaultValueTwoSampleT = valueTwoSampleT;
      let defaultOneSampleProportion = valueOneSampleProportion;
      let defaultTwoSampleProportion = valueTwoSampleProportion;
      const defaultInferentialStatisticsForm = valueInferentialStatisticsForm;
      const defaultAnova = valueAnova;
      let expectedFrequency = '0';
      const frequencyLevels = {};

      if (isFrequencyOrContingency) {
        // set default CHI_SQUARE variable for frequency Table
        defaultInferentialStatistics = frequencyDataSetLength > 1 ? CHI_SQUARE_TEST_FOR_INDEPENDENCE : CHI_SQUARE_GOODNESS_OF_FIT;
        [observedCounts] = categoricalSelectedParametersNames;
        defaultInferentialStatistics = CHI_SQUARE_GOODNESS_OF_FIT;
        frequencyDataset.forEach(frequencyRow => {
          const [data] = values(frequencyRow);
          const n = data.count;
          const observed = data.levels;
          const proportion = 1 / observed.length;
          expectedFrequency = round6(proportion * n);
          frequencyLevels[keys(frequencyRow)] = {};
          observed.forEach(level => {
            const levelName = snakeCase(level.name);
            return (frequencyLevels[keys(frequencyRow)][`level_${levelName}`] = expectedFrequency);
          });
        });
      } else {
        // set default OneSampleT variable if only one variable presented
        if (numericalSummary && numericalSelectedParametersNames.length === 1) {
          const valueDataSet = numericalSummary[numericalSelectedParametersNames[0]];
          const { mean, sampleStandardDeviation, count, name } = valueDataSet;
          defaultValueOneSampleT = {
            sampleSize: count,
            numericalRowDataSet: name,
            sampleMean: round6AndFormatValue(mean),
            standardDeviation: round6AndFormatValue(sampleStandardDeviation)
          };
        }

        // set default OneSampleProportion variable if only one variable presented
        // set default twoSampleProportion variable if only one variable presented
        if (categoricalSummary && categoricalSelectedParametersNames.length === 1) {
          const valueDataSet = categoricalSummary[categoricalSelectedParametersNames[0]];

          const { name, count, levels } = valueDataSet;
          const firstLevel = levels[keys(levels)[0]];
          defaultOneSampleProportion = {
            ...defaultOneSampleProportion,
            numberOfTrials: count,
            categoricalRowDataSet: name
          };
          defaultTwoSampleProportion = {
            ...defaultTwoSampleProportion,
            firstVariable: name,
            numberOfTrialsFirst: count
          };
          if (firstLevel) {
            defaultOneSampleProportion = {
              ...defaultOneSampleProportion,
              numberOfSuccess: firstLevel.count,
              levelsRowDataSet: firstLevel.name
            };
            defaultTwoSampleProportion = {
              ...defaultTwoSampleProportion,
              numberOfSuccessFirst: firstLevel.count,
              successGroupFirst: firstLevel.name
            };
          }
        }

        // set default TwoSampleT variable if only one variable presented
        if (numericalSummary && numericalSelectedParametersNames.length === 1) {
          const valueDataSet = numericalSummary[numericalSelectedParametersNames[0]];
          const { mean, sampleStandardDeviation, count, name } = valueDataSet;

          defaultValueTwoSampleT = {
            ...defaultValueTwoSampleT,
            firstVariable: name,
            sampleSizeFirst: count,
            sampleMeanFirst: round6AndFormatValue(mean),
            standardDeviationFirst: round6AndFormatValue(sampleStandardDeviation)
          };
        }

        // set default PairedT variables
        if (numericalSummary && numericalSelectedParametersNames.length > 0) {
          // set default PairedT variable if dataset has only one variable
          if (numericalSummary && numericalSelectedParametersNames.length === 1) {
            defaultValuePairedT = calculateDefaultValueForPairedT({
              numericalSummary,
              numericalRowDataSet: numericalSelectedParametersNames[0]
            });
          } else {
            dispatch(setHasNumericalVariablesEmptyCell());
            defaultValuePairedT = calculateDefaultValueForPairedT({
              numericalSummary,
              firstAndSecondNumericalVariable,
              numericalSelectedParametersNames
            });
          }
        }

        if (numericalSelectedParametersNames.length > 1 && categoricalSelectedParametersNames.length < 1) {
          dispatch(setHasNumericalVariablesEmptyCell());
          dispatch(setHasCategoricalVariablesEmptyCell());
        }
      }
      initialize({
        inferentialStatistics: defaultInferentialStatistics,
        ...defaultInferentialStatisticsForm,
        oneSampleT: {
          ...defaultValueOneSampleT
        },
        twoSampleT: {
          ...defaultValueTwoSampleT
        },
        oneSampleProportion: {
          ...defaultOneSampleProportion
        },
        twoSampleProportion: {
          ...defaultTwoSampleProportion
        },
        pairedT: {
          ...valuePairedT,
          ...defaultValuePairedT
        },
        chiSquare: {
          observedCounts,
          expectedCounts: EQUAL_PROPORTION,
          ...frequencyLevels
        },
        anova: {
          ...defaultAnova
        }
      });
    }
  }, []);


  const isAnovaDataSetScenario =
    !isFrequencyOrContingency &&
    numericalSelectedParametersNames.length > 0 &&
    categoricalSelectedParametersNames.length > 0;
  const isAnovaPostHocAnalysisTab = inferentialStatistics === ANOVA && activeIndexTabAnova === 1;
  const isAnovaSideBySideBoxPlotsTab = inferentialStatistics === ANOVA && activeIndexTabAnova === 2 && isAnovaDataSetScenario;
  const generateSamplesButtonDisabled = props.invalid || isPairedTInvalid;
  const isProcedureChiSquare =
    inferentialStatistics === CHI_SQUARE_TEST_FOR_INDEPENDENCE ||
    inferentialStatistics === CHI_SQUARE_GOODNESS_OF_FIT ||
    inferentialStatistics === CHI_SQUARE_TEST_FOR_HOMOGENEITY;

  const showGenerateAndResetButtons = !(
    (!isFrequencyOrContingency && isProcedureChiSquare
    ) ||
    (frequencyDataSetLength === 1 && (
      inferentialStatistics === CHI_SQUARE_TEST_FOR_INDEPENDENCE ||
      inferentialStatistics === CHI_SQUARE_TEST_FOR_HOMOGENEITY)
    ) ||
    isAnovaPostHocAnalysisTab ||
    isAnovaSideBySideBoxPlotsTab
  );

  const showTypeProcedureRadioButton =
    !(
      inferentialStatistics === ANOVA || isProcedureChiSquare
    );

  const handleChangeProcedureDropdown = value => {
    // avoid resetting right panel when user click the same option on dropdown
    if (inferentialStatistics === value) {
      return;
    }
    if (isCalculated) {
      dispatch(resetParametersInferentialStatistics());
    }
  };
  const onGenerateSamplesClick = () =>
    dispatch(generateSamples());

  const onResetClick = () => {
    dispatch(handleResetClick());
  };

  const onUpdateAnovaConfidenceIntervalClick = () => {
    dispatch(generateSamples());
  };

  return (
    <div className="content-item" id="settings-form">
      <h2 className="header-page">Settings</h2>
      <Dropdown
        optionsList={optionsInferentialProcedure}
        label="Procedure Selection"
        name="inferentialStatistics"
        placeholder="Select a procedure"
        id="procedureDropdown"
        onChange={handleChangeProcedureDropdown}
      />
      <div>
        {inferentialStatistics === ONE_SAMPLE_T && (
          <FormSection name="oneSampleT">
            <FieldsOneSampleT isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {inferentialStatistics === TWO_SAMPLE_T && (
          <FormSection name="twoSampleT">
            <FieldsTwoSampleT isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {inferentialStatistics === ONE_SAMPLE_PROPORTION && (
          <FormSection name="oneSampleProportion">
            <FieldsOneSampleProportion isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {inferentialStatistics === TWO_SAMPLE_PROPORTION && (
          <FormSection name="twoSampleProportion">
            <FieldsTwoSampleProportion isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {inferentialStatistics === PAIRED_T && (
          <FormSection name="pairedT">
            <FieldsPairedT isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {inferentialStatistics === CHI_SQUARE_GOODNESS_OF_FIT &&
        isFrequencyOrContingency &&
        (
          <FormSection name="chiSquare">
            <FieldsChiSquare
              setValidationChiSquareError={setIsValidationChiSquareError}
              isValidationChiSquareError={isValidationChiSquareError}
            />
          </FormSection>
        )}
        {inferentialStatistics === ANOVA && (
          <FormSection name="anova">
            <FieldsAnova isFrequencyTable={isFrequencyOrContingency} />
          </FormSection>
        )}
        {showTypeProcedureRadioButton && <ProcedureTypeRadioButtons />}
        {inferentialStatistics === TWO_SAMPLE_T && (
          <div className="flex flex-column mb-25 mt-20 mr-10">
            <Field
              type="boolean"
              id="assumeEqualVariances"
              component={CheckBox}
              name="twoSampleT.assumeEqualVariances"
              className="flex baseline mb-5"
            >
              Assume Equal Variances
            </Field>
            <i className="main-input__label">
              Leave unchecked if you are unsure
            </i>
          </div>
        )}
        {showGenerateAndResetButtons && (
          <div className="flex mb-15 mt-15">
            <Button
              submit
              className="main-button--green mr-10"
              onClick={onGenerateSamplesClick}
              disabled={
                generateSamplesButtonDisabled || isValidationChiSquareError
              }
              data-testid="btnGenerateResults"
            >
              Generate Results
            </Button>
            <Button
              reset
              className="main-button--grey"
              onClick={onResetClick}
              data-testid="btnResetResults"
            >
              Reset
            </Button>
          </div>
        )}
        {isAnovaPostHocAnalysisTab && (
          <div className="flex mb-15 mt-15">
            <Button
              className="main-button--green mr-10"
              onClick={onUpdateAnovaConfidenceIntervalClick}
              data-testid="btnUpdateAnovaConfidenceInterval"
              disabled={generateSamplesButtonDisabled}
            >
              Update Confidence Interval
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

SettingsFormComponent.propTypes = {
  dirty: PropTypes.bool.isRequired,
  invalid: PropTypes.bool.isRequired,
  initialize: PropTypes.func.isRequired,
  isFrequencyOrContingency: PropTypes.bool.isRequired
};

export default reduxForm({ form: INFERENTIAL_STATISTICS, destroyOnUnmount: false })(
  SettingsFormComponent
);
